import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { StatusCodes } from 'http-status-codes';
import { EMPTY, from, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { ORG_ROUTES } from '../../routing.const';
import { AuthenticationPolicy } from '../api/api/organization';

/**
 * Interceptor that catches specific API errors related to authentication policies
 * and redirects to a designated authentication gateway if necessary.
 */
@Injectable()
export class OrgAuthGatewayInterceptor implements HttpInterceptor {
  readonly router = inject(Router);
  private redirectInProgress = false;

  private readonly ignoreList = ['customviews/with-badge'];

  constructor() {
    // Reset the redirect flag when navigation completes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => (this.redirectInProgress = false));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError((err) => this.handleError(err)));
  }

  /**
   * Handles errors by checking if they match a specific authentication policy failure,
   * initiating a redirect to the authentication gateway if necessary.
   * @param err - The error response from an HTTP request.
   * @returns Observable that completes if redirected or rethrows the error for further handling.
   */
  private handleError(err: HttpErrorResponse): Observable<HttpEvent<any>> {
    if (this.isAuthenticationPolicyFailure(err) && !this.redirectInProgress) {
      if (new RegExp(this.ignoreList.join('|')).test(err.url)) {
        return throwError(err);
      }

      const orgId = this.extractOrgId(err);
      const targetUrl = `/org/${ORG_ROUTES.AUTH_GATEWAY}/${orgId}`;
      if (this.router.url !== targetUrl) {
        this.redirectInProgress = true;
        return from(
          this.redirectToAuthGateway(
            err.error.detail,
            targetUrl.split('/').slice(1),
          ),
        ).pipe(switchMap(() => EMPTY));
      }

      return EMPTY;
    }

    // Allows other interceptors to handle remaining errors
    return throwError(err);
  }

  /**
   * Checks if the error response is an authentication policy failure.
   * @param err - The error response from an HTTP request.
   * @returns True if the error is a 403 with the type 'authentication-policies-failure'.
   */
  private isAuthenticationPolicyFailure(err: HttpErrorResponse): boolean {
    return (
      err.status === StatusCodes.FORBIDDEN &&
      err.error?.type === 'authentication-policies-failure'
    );
  }

  /**
   * Extracts the organization ID from the error response instance.
   * @param err - The error response from an HTTP request.
   * @returns The organization ID if available; otherwise, undefined.
   */
  private extractOrgId(err: HttpErrorResponse): string | undefined {
    return err.error.instance.split('/')?.[2];
  }

  /**
   * Redirects to the authentication gateway route with the specified query parameters.
   * @param authenticationPolicy - The authentication policy details from the error response.
   * @param route - The route segments for navigating to the target URL.
   * @returns A Promise resolving to true if navigation was successful.
   */
  private redirectToAuthGateway(
    authenticationPolicy: AuthenticationPolicy,
    route: string[],
  ): Promise<boolean> {
    return this.router.navigate(route, {
      queryParams: {
        authenticationPolicy,
      },
    });
  }
}
